import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { Tooltip } from "react-tooltip";
import { pencil } from "@/components/icons/SVGIcons";
import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import {
  parseServerDateTime,
  returnStringFormattedDateTime,
} from "@/lib/globalFormatterHelper";
import clockStore from "@/store/clockStore";
import featureFlagStore from "@/store/featureFlagStore";
import tradeDataStore from "@/store/tradeDataStore";
import ExpiryEditPopover from "./ExpiryEditPopover";
import { IAnchorRect, canEditTradeExpiry } from "./expiryEditHelpers";
import {
  isExpiryLive,
  returnExpiryDisplay,
  returnExpiryStepKey,
  returnExpiryToneStyle,
  returnStaticExpiryDisplay,
} from "./expiryCountdownHelpers";

// LQC-2171/2172/2173 - the ES-level "Expires" cell: a live countdown whose
// granularity tightens as expiry approaches, an urgency tint, and the OP's edit
// pencil. See docs/features/TRADE_EXPIRY.md for the tiers and the reasoning.
//
// The clock subscription selects a QUANTISED STEP KEY, never nowMs. A cell
// counting down in days therefore returns the same key every tick and never
// re-renders; only cells inside the seconds tier wake each second. Selecting
// nowMs instead would re-render every visible cell every second.
const ExpiresCellRenderer = (params: any) => {
  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);
  const esid: string | undefined = fullTradeData?.esid;

  const tradeExpiryEdit = featureFlagStore((state) => state.tradeExpiryEdit);
  const tradeExpiryCountdown = featureFlagStore(
    (state) => state.tradeExpiryCountdown,
  );
  const currentTradeData = tradeDataStore((state) => state.tradeData);
  const thisTrade =
    currentTradeData.find((trade) => trade.esid === esid) ?? fullTradeData;

  // Set on click - the popover opens anchored to the pencil, not centred.
  const [anchorRect, setAnchorRect] = useState<IAnchorRect | null>(null);

  // The API sends expiryEnd without a timezone designator; parseServerDateTime
  // reads it as UTC so the countdown isn't hours out. Never `new Date()` here.
  const rawExpiry = thisTrade?.expiryEnd ?? params?.value;
  const expiryEnd = useMemo(() => parseServerDateTime(rawExpiry), [rawExpiry]);
  const isLive = isExpiryLive(thisTrade?.status);

  // Flag off returns a constant, so the cell never re-renders on a tick and
  // falls back to the static timestamp below (the app clock is stopped too, see
  // App.tsx - this selector is the only thing that would still run).
  const stepKey = clockStore((state) =>
    tradeExpiryCountdown
      ? returnExpiryStepKey(expiryEnd, state.nowMs, isLive)
      : "countdownOff",
  );

  // Rebuilds the label only when the step key moves, so no string is formatted
  // on a tick that can't change the text.
  const display = useMemo(
    () =>
      tradeExpiryCountdown
        ? returnExpiryDisplay(expiryEnd, clockStore.getState().nowMs, isLive)
        : returnStaticExpiryDisplay(expiryEnd),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stepKey, expiryEnd, isLive, tradeExpiryCountdown],
  );

  const showEditPencil =
    !!esid &&
    canEditTradeExpiry(thisTrade?.role, tradeExpiryEdit, thisTrade?.status);
  const tooltipId = `expiresCellEdit-${esid}`;
  // While the cell shows a countdown, the absolute timestamp is still one hover
  // away (and copy/paste always yields it via the valueFormatter).
  const absoluteExpiry = expiryEnd
    ? returnStringFormattedDateTime(expiryEnd)
    : "";

  const openPopover = (event: React.MouseEvent<HTMLSpanElement>) => {
    const { top, left, bottom, right } =
      event.currentTarget.getBoundingClientRect();
    setAnchorRect({ top, left, bottom, right });
  };

  return (
    <div
      data-testid="ExpiresCellContainer"
      id="ExpiresCellContainer"
      data-expiry-tier={display.tier}
      className="flex h-full w-full items-center gap-1 rounded px-1"
      style={returnExpiryToneStyle(display.tone)}
    >
      <span
        data-testid="ExpiresCellValue"
        data-tooltip-id={absoluteExpiry ? tooltipId : undefined}
        data-tooltip-content={absoluteExpiry}
        className="overflow-hidden text-ellipsis whitespace-nowrap"
      >
        {display.label}
      </span>
      {showEditPencil && (
        <span
          data-testid="ExpiresCellEditButton"
          id={`ExpiresCellEditButton-${esid}`}
          data-tooltip-id={tooltipId}
          data-tooltip-content="Click to edit expiry"
          aria-label="Edit trade expiry"
          role="button"
          // Dull until the row is hovered, matching the settings cog.
          className={clsx(
            "ml-auto inline-flex shrink-0 cursor-pointer items-center justify-center transition-all",
            "text-gray-500 opacity-40 hover:text-orange-500 hover:opacity-100",
            "dark:text-gray-400 dark:hover:text-orange-500 dark:hover:opacity-100",
            "[.ag-row-hover_&]:opacity-100",
          )}
          onClick={openPopover}
        >
          {pencil}
        </span>
      )}
      {/* Portalled to the body so the tooltip sits over the grid instead of
          being clipped by the cell. */}
      {createPortal(
        <Tooltip id={tooltipId} place="left" className="z-[9999] !text-xs" />,
        document.body,
      )}
      {anchorRect && esid && (
        <ExpiryEditPopover
          esid={esid}
          expiryEnd={rawExpiry}
          anchorRect={anchorRect}
          onClose={() => setAnchorRect(null)}
        />
      )}
    </div>
  );
};

export default ExpiresCellRenderer;
